import {
  Stack,
  TextField,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";

interface CompetenceSelectorProps {
  competenceType: "single" | "range";
  singleMonth: string;
  rangeStart: string;
  rangeEnd: string;
  occupiedPeriods?: Set<string>;
  onCompetenceTypeChange: (type: "single" | "range") => void;
  onSingleMonthChange: (month: string) => void;
  onRangeStartChange: (start: string) => void;
  onRangeEndChange: (end: string) => void;
}

const months = [
  { value: "01", label: "Janeiro" },
  { value: "02", label: "Fevereiro" },
  { value: "03", label: "Março" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Maio" },
  { value: "06", label: "Junho" },
  { value: "07", label: "Julho" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

const getAvailableMonths = (
  year: number,
  startYear?: number,
  startMonth?: string,
  isEndMonth?: boolean,
) => {
  const currentDate = dayjs();
  const currentYear = currentDate.year();
  const currentMonth = currentDate.month() + 1;

  if (year > currentYear) {
    return [];
  }

  let availableMonths = months;

  if (year === currentYear) {
    availableMonths = months.filter((m) => parseInt(m.value) < currentMonth);
  }

  if (isEndMonth && startYear && startMonth) {
    if (year === startYear) {
      availableMonths = availableMonths.filter(
        (m) => parseInt(m.value) >= parseInt(startMonth),
      );
    } else if (year < startYear) {
      return [];
    }
  }

  return availableMonths;
};

const parseCompetence = (value: string) => {
  const [month, year] = value.split("/");
  return { month, year: parseInt(year) };
};

const formatCompetence = (month: string, year: number) => {
  return `${month}/${year}`;
};

const getAvailableYears = () => {
  const currentYear = dayjs().year();
  const startYear = 2015;
  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

const CompetenceSelector = ({
  competenceType,
  singleMonth,
  rangeStart,
  rangeEnd,
  occupiedPeriods = new Set(),
  onCompetenceTypeChange,
  onSingleMonthChange,
  onRangeStartChange,
  onRangeEndChange,
}: CompetenceSelectorProps) => {
  const availableYears = getAvailableYears();

  const isPeriodOccupied = (month: string, year: number) => {
    const periodKey = `${year}-${month.padStart(2, "0")}`;
    return occupiedPeriods.has(periodKey);
  };

  return (
    <FormControl component="fieldset">
      <RadioGroup
        row
        value={competenceType}
        onChange={(e) =>
          onCompetenceTypeChange(e.target.value as "single" | "range")
        }
      >
        <FormControlLabel value="single" control={<Radio />} label="Mensal" />
        <FormControlLabel value="range" control={<Radio />} label="Período" />
      </RadioGroup>

      {competenceType === "single" && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              select
              label="Mês"
              value={parseCompetence(singleMonth).month || ""}
              onChange={(e) => {
                const { year } = parseCompetence(singleMonth);
                onSingleMonthChange(formatCompetence(e.target.value, year));
              }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {getAvailableMonths(
                parseCompetence(singleMonth).year || new Date().getFullYear(),
              ).map((month) => {
                const isOccupied = isPeriodOccupied(
                  month.value,
                  parseCompetence(singleMonth).year || new Date().getFullYear(),
                );
                return (
                  <MenuItem
                    key={month.value}
                    value={month.value}
                    disabled={isOccupied}
                  >
                    {month.label} {isOccupied && "(Já registrado!)"}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              label="Ano"
              value={parseCompetence(singleMonth).year || ""}
              onChange={(e) => {
                const { month } = parseCompetence(singleMonth);
                const year = parseInt(e.target.value);
                onSingleMonthChange(formatCompetence(month || "01", year));
              }}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      )}

      {competenceType === "range" && (
        <Stack spacing={3} sx={{ mt: 3 }}>
          <Stack spacing={2}>
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.secondary"
            >
              Período inicial
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                select
                label="Mês inicial"
                value={parseCompetence(rangeStart).month}
                onChange={(e) => {
                  const { year } = parseCompetence(rangeStart);
                  const newStart = formatCompetence(e.target.value, year);
                  onRangeStartChange(newStart);

                  const end = parseCompetence(rangeEnd);
                  const start = parseCompetence(newStart);
                  if (
                    end.year < start.year ||
                    (end.year === start.year &&
                      parseInt(end.month) < parseInt(start.month))
                  ) {
                    onRangeEndChange(newStart);
                  }
                }}
                size="small"
                sx={{ minWidth: 200 }}
              >
                {getAvailableMonths(
                  parseCompetence(rangeStart).year || new Date().getFullYear(),
                ).map((month) => {
                  const isOccupied = isPeriodOccupied(
                    month.value,
                    parseCompetence(rangeStart).year ||
                      new Date().getFullYear(),
                  );
                  return (
                    <MenuItem
                      key={month.value}
                      value={month.value}
                      disabled={isOccupied}
                    >
                      {month.label} {isOccupied && "(Já registrado!)"}
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                select
                label="Ano inicial"
                value={parseCompetence(rangeStart).year || ""}
                onChange={(e) => {
                  const { month } = parseCompetence(rangeStart);
                  const year = parseInt(e.target.value);
                  const newStart = formatCompetence(month || "01", year);
                  onRangeStartChange(newStart);

                  const end = parseCompetence(rangeEnd);
                  const start = parseCompetence(newStart);
                  if (
                    end.year < start.year ||
                    (end.year === start.year &&
                      parseInt(end.month) < parseInt(start.month))
                  ) {
                    onRangeEndChange(newStart);
                  }
                }}
                size="small"
                sx={{ minWidth: 150 }}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
          <Stack spacing={2}>
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.secondary"
            >
              Período final
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                select
                label="Mês final"
                value={parseCompetence(rangeEnd).month || ""}
                onChange={(e) => {
                  const { year } = parseCompetence(rangeEnd);
                  onRangeEndChange(formatCompetence(e.target.value, year));
                }}
                size="small"
                sx={{ minWidth: 200 }}
                disabled={
                  parseCompetence(rangeEnd).year <
                    parseCompetence(rangeStart).year ||
                  (parseCompetence(rangeEnd).year ===
                    parseCompetence(rangeStart).year &&
                    parseInt(parseCompetence(rangeEnd).month || "01") <
                      parseInt(parseCompetence(rangeStart).month || "01"))
                }
              >
                {getAvailableMonths(
                  parseCompetence(rangeEnd).year || new Date().getFullYear(),
                  parseCompetence(rangeStart).year || new Date().getFullYear(),
                  parseCompetence(rangeStart).month || "01",
                  true,
                ).map((month) => {
                  const isOccupied = isPeriodOccupied(
                    month.value,
                    parseCompetence(rangeEnd).year || new Date().getFullYear(),
                  );
                  return (
                    <MenuItem
                      key={month.value}
                      value={month.value}
                      disabled={isOccupied}
                    >
                      {month.label} {isOccupied && "(Já registrado!)"}
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                select
                label="Ano final"
                value={parseCompetence(rangeEnd).year || ""}
                onChange={(e) => {
                  const { month } = parseCompetence(rangeEnd);
                  const year = parseInt(e.target.value);
                  const newEnd = formatCompetence(month || "01", year);
                  const start = parseCompetence(rangeStart);

                  if (
                    year < start.year ||
                    (year === start.year &&
                      parseInt(month || "01") < parseInt(start.month || "01"))
                  ) {
                    onRangeEndChange(
                      formatCompetence(start.month || "01", start.year),
                    );
                  } else {
                    onRangeEndChange(newEnd);
                  }
                }}
                size="small"
                sx={{ minWidth: 150 }}
              >
                {availableYears
                  .filter(
                    (year) =>
                      year >=
                      (parseCompetence(rangeStart).year ||
                        new Date().getFullYear()),
                  )
                  .map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> Cada arquivo deve conter apenas um mês.
              Não envie documentos com informações de meses diferentes no mesmo
              arquivo.
            </Typography>
          </Alert>
        </Stack>
      )}
    </FormControl>
  );
};

export default CompetenceSelector;
